import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Loader2, Home, Building2, CalendarOff, Plane, MapPin } from "lucide-react";

const WD = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const ABS_LABEL = { vacation: "Vacaciones", sick: "Baja médica", personal: "Asuntos propios", other: "Otros" };
const pad = (n) => String(n).padStart(2, "0");

function* eachDay(start, end) {
  const d = new Date(start);
  while (d <= end) { yield new Date(d); d.setDate(d.getDate() + 1); }
}

export default function Calendar() {
  const [ref, setRef] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const monthKey = `${ref.getFullYear()}-${pad(ref.getMonth() + 1)}`;

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/calendar?month=${monthKey}`);
    setData(data); setLoading(false);
  }, [monthKey]);
  useEffect(() => { load(); }, [load]);

  // Bucket data per day
  const byDay = useMemo(() => {
    const map = {};
    const ensure = (k) => (map[k] = map[k] || { shifts: [], office: 0, remote: 0, absences: [], holiday: null });
    (data?.shifts || []).forEach((s) => {
      const d = ensure(s.date);
      d.shifts.push(s);
      if (s.work_mode === "remote") d.remote++; else d.office++;
    });
    (data?.holidays || []).forEach((h) => { ensure(h.date).holiday = h; });
    (data?.absences || []).forEach((a) => {
      for (const day of eachDay(new Date(a.start_date + "T00:00:00"), new Date(a.end_date + "T00:00:00"))) {
        if (day.getMonth() !== ref.getMonth() || day.getFullYear() !== ref.getFullYear()) continue;
        const k = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
        ensure(k).absences.push(a);
      }
    });
    return map;
  }, [data, ref]);

  // Build calendar grid (Mon-first)
  const cells = useMemo(() => {
    const year = ref.getFullYear(), month = ref.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [ref]);

  const todayKey = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Calendario</h1>
          <p className="text-slate-500 text-sm mt-1">Vista mensual del equipo: fichajes, teletrabajo, ausencias y festivos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" data-testid="cal-prev" onClick={() => setRef(new Date(ref.getFullYear(), ref.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[170px] text-center font-heading font-semibold capitalize" data-testid="cal-month">
            {ref.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
          </div>
          <Button size="icon" variant="outline" data-testid="cal-next" onClick={() => setRef(new Date(ref.getFullYear(), ref.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-sky-500" /> Presencial</span>
        <span className="flex items-center gap-1.5"><Home className="h-3.5 w-3.5 text-violet-500" /> Teletrabajo</span>
        <span className="flex items-center gap-1.5"><Plane className="h-3.5 w-3.5 text-amber-500" /> Ausencia</span>
        <span className="flex items-center gap-1.5"><CalendarOff className="h-3.5 w-3.5 text-rose-500" /> Festivo</span>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>
      ) : (
        <Card className="p-3 sm:p-5 border-slate-200 shadow-none">
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {WD.map((d) => <div key={d} className="text-center text-[11px] font-bold uppercase tracking-wider text-slate-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((day, i) => {
              if (!day) return <div key={i} className="min-h-[88px] rounded-lg bg-slate-50/40" />;
              const k = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
              const info = byDay[k];
              const isToday = k === todayKey;
              return (
                <button
                  key={i}
                  data-testid={`cal-day-${k}`}
                  onClick={() => info && setSelected({ key: k, date: day, ...info })}
                  className={`min-h-[88px] rounded-lg border p-1.5 text-left transition-all hover:border-slate-300 ${info?.holiday ? "bg-rose-50/60 border-rose-100" : "bg-white border-slate-100"} ${isToday ? "ring-2 ring-[var(--brand)] ring-offset-1" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold ${isToday ? "text-[var(--brand)]" : "text-slate-600"}`}>{day.getDate()}</span>
                    {info?.shifts?.length > 0 && <span className="text-[10px] text-slate-400 font-medium">{info.shifts.length}</span>}
                  </div>
                  {info?.holiday && <div className="mt-1 text-[10px] font-medium text-rose-600 truncate">{info.holiday.name}</div>}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {info?.office > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] rounded bg-sky-50 text-sky-600 px-1 py-0.5 font-medium"><Building2 className="h-2.5 w-2.5" />{info.office}</span>}
                    {info?.remote > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] rounded bg-violet-50 text-violet-600 px-1 py-0.5 font-medium"><Home className="h-2.5 w-2.5" />{info.remote}</span>}
                    {info?.absences?.length > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] rounded bg-amber-50 text-amber-600 px-1 py-0.5 font-medium"><Plane className="h-2.5 w-2.5" />{info.absences.length}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading capitalize">
              {selected?.date?.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
            </DialogTitle>
          </DialogHeader>
          {selected?.holiday && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-50 border border-rose-100 text-sm text-rose-700">
              <CalendarOff className="h-4 w-4" /> Festivo: <b>{selected.holiday.name}</b>
            </div>
          )}
          {selected?.absences?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Ausencias</div>
              <div className="space-y-1.5">
                {selected.absences.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700">{a.employee_name}</span>
                    <Badge variant="secondary" className="text-xs">{ABS_LABEL[a.type] || a.type}{a.status === "pending" ? " · pendiente" : ""}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          {selected?.shifts?.length > 0 ? (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Fichajes ({selected.shifts.length})</div>
              <div className="space-y-1.5">
                {selected.shifts.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700 flex items-center gap-2">
                      {s.work_mode === "remote" ? <Home className="h-3.5 w-3.5 text-violet-500" /> : <Building2 className="h-3.5 w-3.5 text-sky-500" />}
                      {s.employee_name}
                      {s.location?.lat != null && (
                        <a href={`https://www.google.com/maps?q=${s.location.lat},${s.location.lng}`} target="_blank" rel="noreferrer" className="text-sky-600 inline-flex items-center gap-0.5 hover:underline" data-testid={`shift-map-${i}`} title="Ver ubicación del fichaje">
                          <MapPin className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </span>
                    <span className="text-slate-500 tabular-nums">{Math.floor(s.worked_minutes / 60)}h {pad(Math.round(s.worked_minutes % 60))}m</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (!selected?.absences?.length && !selected?.holiday && <div className="text-sm text-slate-400 text-center py-4">Sin actividad este día.</div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
